import type { GeminiAnalysisResult } from "src/components/Dashboard/CallLogs/types";
import { Alert } from "@heroui/react";
import { capitalize } from "lodash";
import { cn } from "@/lib/utils";
import { Button } from "../../ui/Button";

interface IssuesListProps {
  issues: Array<GeminiAnalysisResult["issues"][0]>;
}

export function IssuesList({ issues }: IssuesListProps) {
  const getColor = (issue: GeminiAnalysisResult["issues"][0]) => {
    switch (issue.severity) {
      case "medium":
        return "warning";
      case "high":
        return "danger";
      default:
        return "primary";
    }
  };

  // We sort by highest to lowest severity high -> medium -> low
  const sortedIssues = issues.sort((a, b) => {
    if (a.severity === "high" && b.severity !== "high") {
      return -1;
    }
    if (a.severity !== "high" && b.severity === "high") {
      return 1;
    }
    if (a.severity === "medium" && b.severity !== "medium") {
      return -1;
    }
    if (a.severity !== "medium" && b.severity === "medium") {
      return 1;
    }
    if (a.severity === "low" && b.severity !== "low") {
      return -1;
    }
    if (a.severity !== "low" && b.severity === "low") {
      return 1;
    }
    return 0;
  });

  return sortedIssues.map((issue) => {
    return (
      <div
        className="flex w-full items-center justify-center"
        key={issue.detail + issue.startSec + issue.endSec}
      >
        <Alert
          radius="sm"
          color={getColor(issue)}
          title={capitalize(issue.type.replace(/_/g, " "))}
          description={issue.detail}
          classNames={{
            title: "text-xs font-semibold",
            description: "text-xs mt-1",
            base: cn([
              `bg-default-50 shadow-xs`,
              `
                border-1 border-default-200
                dark:border-default-100
              `,
              `
                relative
                before:absolute before:z-10 before:content-[\'\']
              `,
              "before:-top-px before:-bottom-px before:left-0 before:w-1",
              "rounded-l-none border-l-0",
              `
                before:bg-${getColor(issue)}
              `,
            ]),
          }}
          endContent={
            <div className="flex h-full items-center justify-center pl-1">
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-opacity-50"
                onClick={() => {
                  const audioPlayerElement = document.querySelector(
                    ".audio-player-container",
                  );
                  if (audioPlayerElement) {
                    const seekEvent = new CustomEvent("seek-to-timestamp", {
                      detail: { timestamp: issue.startSec },
                    });
                    audioPlayerElement.dispatchEvent(seekEvent);
                  }
                }}
              >
                Listen
              </Button>
            </div>
          }
        />
      </div>
    );
  });
}
